import * as React from 'react'
import { FunctionComponent, ReactElement } from 'react'
import { DetailsRow, IColumn, IconButton, IDetailsRowProps } from '@fluentui/react'
import LegacyLocalDatePicker, { LocalDatePickerProps } from '../../../components/LegacyLocalDatePicker'
import { Formik, FormikConfig, useFormikContext } from 'formik'
import { RegistrationMutationDto, RegistrationOverviewDto } from '../../../config/registrationConfig'
import { RegistrationItemProps } from './RegistrationList'
import { makeStyles } from '@fluentui/react-components'
import FormikField from '../../Formik/FormikField'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import UserCombobox from '../../Combobox/UserCombobox'
import { SubComboboxProps } from '../../Combobox/BaseCombobox'
import { UserListItemDto } from '../../../config/userConfig'
import FormikInputField from '../../Formik/FormikInputField'

export const useRegistrationRowStyles = makeStyles({
    rate: {
        width: '78px',
        minWidth: 'auto',
    },
    rateInput: {
        width: '78px',
    },
    description: {
        width: '100%',
    },
    dateInput: {
        width: '48px',
    },
    user: {
        width: '60px',
        minWidth: 'auto',
    },
    userInput: {
        width: '24px',
    },
    vat: {
        width: '96px',
        minWidth: 'auto',
    },
    vatInput: {
        width: '63px',
    },
    type: {
        width: '120px',
        minWidth: 'auto',
    },
    typeWide: {
        width: '200px',
        minWidth: 'auto',
    },
    typeInput: {
        width: '86px',
    },
    typeWideInput: {
        width: '166px',
    },
})

export type ModifyRegistrationRowProps<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I = {}> = {
    listProps: RegistrationItemProps<I>
    rowProps: Omit<IDetailsRowProps, 'item'> & {
        item: Partial<D | RegistrationOverviewDto> & Pick<RegistrationOverviewDto, 'user'>
    }
    formikProps: FormikConfig<M>
}

export type ModifyRegistrationColumnProps<I = {}> = {
    column?: IColumn
} & RegistrationItemProps<I>

type Props<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I> = {
    columns: FunctionComponent<ModifyRegistrationColumnProps<I>>
} & ModifyRegistrationRowProps<D, M, I>

export function ModifyRegistrationColumns<M extends RegistrationMutationDto, I = {}>(
    props: ModifyRegistrationColumnProps<I>,
): ReactElement {
    const styles = useRegistrationRowStyles()
    const { values, setFieldValue, submitForm } = useFormikContext<M>()

    switch (props.column.key) {
        case 'user':
            return (
                <FormikComboboxField<M, SubComboboxProps<UserListItemDto>>
                    required
                    fieldName={'userId'}
                    component={UserCombobox}
                    componentProps={{
                        size: 'small',
                        getEntityLabel: entity => entity.shortCode,
                        className: styles.user,
                        input: { className: styles.userInput },
                    }}
                />
            )
        case 'date':
            return (
                <FormikField<LocalDatePickerProps, M>
                    required
                    fieldName={'date'}
                    component={LegacyLocalDatePicker}
                    componentProps={{
                        isListColumn: true,
                        onSelectDate: date => setFieldValue('date', date),
                        datePickerProps: {
                            placeholder: undefined,
                            className: styles.dateInput,
                        },
                    }}
                />
            )
        case 'description':
            return (
                <FormikInputField<M>
                    required
                    fieldName={'description'}
                    componentProps={{
                        size: 'small',
                        className: styles.description,
                    }}
                />
            )
        case 'rate':
            return (
                <FormikField<MoneyInputProps, M>
                    required
                    fieldName={'rate'}
                    component={MoneyInput}
                    componentProps={{
                        size: 'small',
                        currency: values.currency,
                        allowNegative: true,
                        onCommit: (_, value: string) => {
                            setFieldValue('rate', value)
                        },
                        className: styles.rate,
                        input: { className: styles.rateInput },
                    }}
                />
            )
        case 'actions':
            return <IconButton iconProps={{ iconName: 'Save' }} onClick={submitForm} />
    }

    return null
}

export default function ModifyRegistrationRow<
    D extends RegistrationOverviewDto,
    M extends RegistrationMutationDto,
    I = {},
>(props: Props<D, M, I>): ReactElement {
    return (
        <Formik<M> {...props.formikProps}>
            <DetailsRow
                {...props.rowProps}
                compact
                onRenderItemColumn={(_item, _index?: number, column?: IColumn) => (
                    <props.columns {...props.listProps} column={column} />
                )}
            />
        </Formik>
    )
}
